import React, { useState, useEffect } from "react";
import { Modal, ModalBody, Row, Col, Alert } from "reactstrap";
import { Button, Icon } from "./Component";
import FormInput from "../pages/form-components/InputComponent";
import { FormProvider, useForm } from "react-hook-form";
import contractorsApis from "../api/master/contractors";

interface GstinVerificationModalProps {
  isOpen: boolean;
  toggle: () => void;
  onVerified: (data: any) => void;
  countries: Array<{ value: string; label: string }>;
  states: Array<{ value: string; label: string }>;
  districts: Array<{ value: string; label: string }>;
  source?: "contractors" | "suppliers" | "clients";
}

const GstinVerificationModal: React.FC<GstinVerificationModalProps> = ({
  isOpen,
  toggle,
  onVerified,
  countries,
  states,
  districts,
  source = "contractors",
}) => {
  const methods = useForm({
    mode: "onSubmit",
    reValidateMode: "onChange",
  });
  const [file, setFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<any>(null);
  const [error, setError] = useState<string>("");
  const [uploading, setUploading] = useState<boolean>(false);
  const [verificationStatus, setVerificationStatus] = useState<{
    type: "success" | "error" | "warning" | "";
    message: string;
  }>({ type: "", message: "" });
  const [isVerifying, setIsVerifying] = useState<boolean>(false);

  useEffect(() => {
    if (!isOpen) {
      setFile(null);
      setPreviewData(null);
      setError("");
      setVerificationStatus({ type: "", message: "" });
      methods.reset();
    }
  }, [isOpen, methods]);

  useEffect(() => {
    if (previewData) {
      methods.reset({
        gstin: previewData.gstin,
        pan: previewData.pan,
        companyName: previewData.LegalName,
        country: previewData.country,
        state: previewData.state,
        district: previewData.district,
        city: previewData.city,
        area: previewData.area,
        addressLine1: previewData.addressLine1,
        zipPincode: previewData.zipPincode,
      });
    }
  }, [previewData, methods]);

  const handleFileUpload = async (e: any) => {
    const uploaded = e.target.files?.[0];
    if (!uploaded) return;
    setError("");
    setVerificationStatus({ type: "", message: "" });
    setFile(uploaded);
    setUploading(true);
    try {
      const response = await contractorsApis.uploadGstFile(uploaded);
      if (response.data?.error) {
        setPreviewData(null);
        setError("Please re-upload a valid GST certificate PDF.");
        setVerificationStatus({ type: "error", message: response.data.error });
        setFile(null);
        setUploading(false);
        return;
      }
      if (response.data && response.data.extracted_data) {
        const d = response.data.extracted_data;

        // Convert state and district names to IDs for dropdowns
        const stateId = d.state ? findOptionValueByLabel(d.state, states) : "";
        const districtId = d.district
          ? findOptionValueByLabel(d.district, districts)
          : "";
        const countryId = d.country
          ? findOptionValueByLabel(d.country, countries)
          : "";

        const mappedData = {
          gstin: d.gstin || "",
          pan: d.pan || "",
          LegalName: d.LegalName || "",
          TradeName: d.TradeName || "",
          country: d.country || "",
          countryId: countryId,
          state: d.state || "",
          stateId: stateId,
          district: d.district || "",
          districtId: districtId,
          city: d.city || "",
          area: d.area || "",
          addressLine1: d.addressLine1 || d.addressline || "",
          zipPincode: d.zipPincode || "",
        };

        if (!mappedData.gstin || !mappedData.LegalName) {
          setError(
            "Critical GST fields could not be extracted. Please re-upload a clear certificate."
          );
          setVerificationStatus({
            type: "error",
            message: "Critical GST fields missing",
          });
          setPreviewData(null);
          setFile(null);
          setUploading(false);
          return;
        }
        const formResetData = {
          gstin: mappedData.gstin,
          pan: mappedData.pan,
          companyName: mappedData.LegalName,
          tradeName: mappedData.TradeName,
          country: countryId || mappedData.country,
          state: stateId || mappedData.state,
          district: districtId || mappedData.district,
          city: mappedData.city,
          area: mappedData.area,
          addressLine1: mappedData.addressLine1,
          zipPincode: mappedData.zipPincode,
        };

        methods.reset(formResetData);
        setPreviewData(mappedData);
        setError("");
        setVerificationStatus({
          type: "success",
          message: "GST details extracted successfully",
        });
      } else {
        setPreviewData(null);
        setError("Please re-upload a valid GST certificate PDF.");
        setVerificationStatus({
          type: "error",
          message: "Invalid GST certificate",
        });
        setFile(null);
      }
    } catch (err) {
      setPreviewData(null);
      setError("Error uploading file: " + "Unknown error");
      setVerificationStatus({ type: "error", message: "Upload failed" });
      setFile(null);
    }
    setUploading(false);
  };

  const handleVerify = async () => {
    const verifyFormData = methods.getValues();

    if (
      !previewData ||
      !previewData.gstin ||
      !previewData.LegalName ||
      !verifyFormData.gstin ||
      !verifyFormData.companyName
    ) {
      setVerificationStatus({
        type: "error",
        message:
          "Please provide a GST file with both GSTIN and Organization Name.",
      });
      return;
    }

    const legalVerifyBody = {
      orgName: verifyFormData.companyName,
      gstin: verifyFormData.gstin,
    };

    setIsVerifying(true);
    setVerificationStatus({ type: "", message: "" });

    try {
      const verifyRes = await contractorsApis.verifyGstDetails(legalVerifyBody);
      const verificationData = verifyRes?.verificationResponse?.[0];
      const verified =
        verificationData?.verified === true ||
        verificationData?.verified === "true";

      if (verified) {
        setVerificationStatus({
          type: "success",
          message: "GSTIN verification successful!",
        });

        const verifiedData = {
          gstin: verificationData.gstin || "",
          pan: verificationData.pan || "",
          LegalName: previewData.LegalName || "",
          TradeName: previewData.TradeName || "",
          country: previewData.country,
          state: verificationData.companyAddress?.stcd || previewData.state,
          district:
            verificationData.companyAddress?.dst || previewData.district,
          city: verificationData.companyAddress?.loc || previewData.city,
          area: verificationData.companyAddress?.st || previewData.area,
          addressLine1: `${verificationData.companyAddress?.bno || ""} ${
            verificationData.companyAddress?.st || ""
          }, ${verificationData.companyAddress?.loc || ""}`.trim(),
          zipPincode:
            verificationData.companyAddress?.pncd || previewData.zipPincode,
          incorporationDate: verificationData.dateOfRegistration || "",
        };

        setTimeout(() => {
          onVerified(verifiedData);
          setPreviewData(null);
          setFile(null);
          toggle();
        }, 1500);
      } else {
        setVerificationStatus({
          type: "error",
          message: "GSTIN verification failed. Please check the details.",
        });
      }
    } catch (apiErr) {
      let errorMessage = "API error occurred.";
      if (typeof apiErr === "object" && apiErr !== null) {
        if (
          "response" in apiErr &&
          typeof (apiErr as any).response?.data?.message === "string"
        ) {
          errorMessage = (apiErr as any).response.data.message;
        } else if (
          "message" in apiErr &&
          typeof (apiErr as any).message === "string"
        ) {
          errorMessage = (apiErr as any).message;
        }
      }
      setVerificationStatus({ type: "error", message: errorMessage });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleCancel = () => {
    setFile(null);
    setPreviewData(null);
    setVerificationStatus({ type: "", message: "" });
    toggle();
  };

  const getDisplayName = (
    id: string,
    options: Array<{ value: string; label: string }>
  ) => {
    const found = options.find((option) => option.value === id);
    return found ? found.label : id;
  };

  const findOptionValueByLabel = (
    label: string,
    options: Array<{ value: string; label: string }>
  ): string => {
    if (!label || !options || options.length === 0) return "";
    const exactMatch = options.find(
      (option) => option.label.toLowerCase() === label.toLowerCase()
    );
    if (exactMatch) return exactMatch.value;

    const partialMatch = options.find(
      (option) =>
        option.label.toLowerCase().includes(label.toLowerCase()) ||
        label.toLowerCase().includes(option.label.toLowerCase())
    );
    return partialMatch ? partialMatch.value : "";
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="xl"
      centered
      backdrop="static"
      keyboard={false}
    >
      <ModalBody>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">GSTIN Verification</h5>
          <button
            type="button"
            className="close"
            onClick={handleCancel}
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        {verificationStatus.message && (
          <Alert
            color={
              verificationStatus.type === "success"
                ? "success"
                : verificationStatus.type === "error"
                ? "danger"
                : "warning"
            }
            className="mb-3"
          >
            {verificationStatus.message}
          </Alert>
        )}

        <FormProvider {...methods}>
          <form>
            <Row className="gy-3">
              <Col md="12">
                <label className="form-label">Upload GSTIN Certificate</label>
                <input
                  type="file"
                  className="form-control"
                  accept=".pdf"
                  onChange={handleFileUpload}
                  disabled={uploading}
                />
                {uploading && (
                  <div className="text-info mt-1">Uploading file...</div>
                )}
                {error && <div className="text-danger mt-1">{error}</div>}
              </Col>

              {previewData && (
                <>
                  <Col md="4">
                    <FormInput
                      name="gstin"
                      label="GSTIN"
                      defaultValue={previewData.gstin}
                    />
                  </Col>
                  <Col md="4">
                    <FormInput
                      name="pan"
                      label="PAN"
                      defaultValue={previewData.pan}
                    />
                  </Col>
                  <Col md="4">
                    <FormInput
                      name="companyName"
                      label={
                        source === "contractors"
                          ? "Contractor Name"
                          : source === "clients"
                          ? "Client Name"
                          : "Supplier Name"
                      }
                      defaultValue={previewData.LegalName}
                    />
                  </Col>
                  {previewData.TradeName && (
                    <Col md="4">
                      <FormInput
                        name="tradeName"
                        label="Trade Name"
                        defaultValue={previewData.TradeName}
                      />
                    </Col>
                  )}
                  <Col md="4">
                    <FormInput
                      name="country"
                      label="Country"
                      defaultValue={
                        previewData.country ||
                        getDisplayName(previewData.countryId, countries)
                      }
                    />
                  </Col>
                  <Col md="4">
                    <FormInput
                      name="state"
                      label="State"
                      defaultValue={
                        previewData.state ||
                        getDisplayName(previewData.stateId, states)
                      }
                    />
                  </Col>
                  <Col md="4">
                    <FormInput
                      name="district"
                      label="District"
                      defaultValue={
                        previewData.district ||
                        getDisplayName(previewData.districtId, districts)
                      }
                    />
                  </Col>
                  <Col md="4">
                    <FormInput
                      name="city"
                      label="City"
                      defaultValue={previewData.city}
                    />
                  </Col>
                  <Col md="4">
                    <FormInput
                      name="area"
                      label="Area"
                      defaultValue={previewData.area}
                    />
                  </Col>
                  <Col md="4">
                    <FormInput
                      name="zipPincode"
                      label="ZIP / Pincode"
                      defaultValue={previewData.zipPincode}
                    />
                  </Col>
                  <Col md="12">
                    <FormInput
                      name="addressLine1"
                      label="Address Line 1"
                      defaultValue={previewData.addressLine1}
                    />
                  </Col>
                </>
              )}
            </Row>
          </form>
        </FormProvider>

        <div className="d-flex justify-content-end mt-3">
          <Button color="secondary" onClick={handleCancel} className="me-2">
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleVerify}
            disabled={!previewData || isVerifying}
          >
            {isVerifying ? (
              "Verifying..."
            ) : (
              <>
                <Icon name="check" /> Verify
              </>
            )}
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default GstinVerificationModal;
